import type { PickListReorderEvent, PickListSelectionChangeEvent } from 'primevue/picklist';
import type { VNodeRef } from 'vue';
import { ref } from 'vue';

import type { ITableColumn } from '@modules/general-settings/submodules/table-columns-settings/submodules/edit/types';

export interface IGetBoundaryRectResult {
  first?: DOMRect;
  last?: DOMRect;
}

export interface IPickListAutoScrollComposable {
  registerColumn(id: string | number): VNodeRef;
  registerList(index: number): (el: HTMLElement | null) => void;
  autoScroll(listIndex: number, columns: ITableColumn[], direction: PickListReorderEvent['direction']): void;
  onSelectionChange(event: PickListSelectionChangeEvent): void;
  onReorder(event: PickListReorderEvent): void;
}

export default function usePickListAutoScroll(): IPickListAutoScrollComposable {
  const columnRegistry = new Map<string, HTMLElement>();
  const listContainers = ref<Element[]>([]);
  const selectedColumns = ref<ITableColumn[][]>();

  function registerColumn(id: string | number): VNodeRef {
    return (el) => {
      if (el instanceof HTMLElement) {
        columnRegistry.set(String(id), el);
      } else {
        columnRegistry.delete(String(id));
      }
    };
  }

  function registerList(index: number) {
    return (el: HTMLElement | null) => {
      if (!el)
        return;

      const wrapper = el.querySelector('.p-listbox-list-container');

      if (wrapper) {
        listContainers.value[index] = wrapper;
      }
    };
  }

  function getBoundaryRects(columns: ITableColumn[]): IGetBoundaryRectResult {
    let first: DOMRect | undefined;
    let last: DOMRect | undefined;

    for (const col of columns) {
      const el = columnRegistry.get(String(col.id));

      if (!el)
        continue;

      const option = el.closest('.p-listbox-option');
      const rect = option?.getBoundingClientRect();

      if (!rect)
        continue;

      if (!first || rect.top < first.top) {
        first = rect;
      }

      if (!last || rect.bottom > last.bottom) {
        last = rect;
      }
    }

    return { first, last };
  }

  function autoScroll(
    listIndex: number,
    columns: ITableColumn[],
    direction: PickListReorderEvent['direction'],
  ) {
    const container = listContainers.value[listIndex];

    if (!container)
      return;

    const { first, last } = getBoundaryRects(columns);

    const boundary = direction === 'up' ? first : last;

    if (!boundary)
      return;

    const containerRect = container.getBoundingClientRect();

    const boundaryValue = direction === 'up' ? boundary.top : boundary.bottom;

    const gap = Number.parseInt(
      getComputedStyle(container.querySelector('.p-listbox-list')!)
        .gap || '0',
      10,
    );

    const scrollStep = boundary.height + gap;

    const diff = direction === 'down'
      ? Math.floor(containerRect.bottom - boundaryValue) / scrollStep
      : Math.floor(containerRect.top - boundaryValue) / scrollStep;

    const coefficient = direction === 'down' ? 3 - diff : 3 + diff;

    if (coefficient <= 0)
      return;

    const delta = scrollStep * coefficient;

    container.scroll({
      top: direction === 'down'
        ? container.scrollTop + delta
        : container.scrollTop - delta,
    });
  }

  function onSelectionChange(event: PickListSelectionChangeEvent): void {
    selectedColumns.value = event.value;
  }

  function onReorder(event: PickListReorderEvent): void {
    const { direction, listIndex } = event;

    if (!direction || listIndex == null)
      return;

    const columns = selectedColumns.value?.[listIndex];

    if (!columns)
      return;

    autoScroll(listIndex, columns, direction);
  }

  return {
    registerColumn,
    registerList,
    autoScroll,
    onSelectionChange,
    onReorder,
  };
}
